import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { StringFormatter } from '@repo/toolkit/string'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Typography from '@repo/ui/typography'
import { Fragment } from 'react/jsx-runtime'

import { useNativeCountryName } from '../../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  genderEnumMapping,
  toGenderEnumValue
} from '../../../../lib/types/enums/common/gender.enum'
import { taughtLevelEnumMapping } from '../../../../lib/types/enums/common/taught-level.enum'
import { languageEnumMapping } from '../../../../lib/types/enums/languages/language.enum'
import { Tefl } from '../../../../lib/types/models/tefl/tefl'
import BirthDateWithAge from '../../../elements/birth-date-with-age'
import DisplayImageInfo from '../../../elements/display-image-info'
import LabelWithText from '../../../elements/label-with-text'
import PhoneNumberDisplay from '../../../elements/phone-number-display'
import OlTeflSejourTable from './ol-tefl-sejour-table'

type Props = {
  tefl: Tefl
  olId: number
}

const boxWrapperClassName = 'bg-white-hover rounded-lg border border-grey-light'

const OlTeflDetails: React.FC<Props> = ({ tefl, olId }) => {
  const nativeCountryName = useNativeCountryName()
  const languageMapping = languageEnumMapping()
  const genderMapping = genderEnumMapping()
  const { t } = useTranslate()

  return (
    <Fragment>
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 w-full flex flex-col gap-4">
          <Box className={boxWrapperClassName}>
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              Détail
            </Typography>
            <div className="h-4" />
            <div className="grid grid-cols-5 gap-3">
              <LabelWithText label="Sexe" data={genderMapping[toGenderEnumValue(tefl.gender)]} />
              <LabelWithText label="NOM" data={tefl.lastName.toUpperCase()} />
              <LabelWithText label="Prénom" data={StringFormatter.capitalize(tefl.firstName)} />
              <LabelWithText
                label="Numéro de téléphone"
                data={<PhoneNumberDisplay phoneNumber={tefl.phoneNumber} />}
              />
              <LabelWithText label="Email" data={tefl.email} className="break-all" />

              <BirthDateWithAge birthDate={tefl.birthDate} />
              <div />
              <LabelWithText label="Nationalité" className="-mb-4" data={tefl.nationality} />
              <LabelWithText
                label="Langue maternelle"
                data={languageMapping[tefl.motherTongue].label}
              />
            </div>
          </Box>
          <Box className={`${boxWrapperClassName} h-full`}>
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              Diplômes TEFL
            </Typography>
            <div className="h-4" />
            <div className="flex flex-col gap-4">
              {tefl.diplomas.length === 0 ? (
                <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
                  Aucun diplôme
                </Typography>
              ) : (
                tefl.diplomas.map((diploma, index) => (
                  <div className="flex flex-col gap-2" key={index}>
                    <div className="flex gap-3">
                      <div className="rounded-full bg-secondary-light h-6 w-6 p-2 flex items-center justify-center">
                        <Typography
                          variant="label-semibold"
                          color={tailwindColors.secondary.DEFAULT}
                        >
                          {index + 1}
                        </Typography>
                      </div>
                      <div className="grid grid-cols-3 gap-5">
                        <LabelWithText label="Diplôme" data={diploma.name} />
                        <LabelWithText
                          label="Année d'obtention"
                          data={diploma.yearOfObtention.toString()}
                        />
                      </div>
                    </div>
                    <DisplayImageInfo file={diploma.file} label={diploma.name} />
                  </div>
                ))
              )}
            </div>
          </Box>
        </div>
        <div className="flex flex-col gap-4">
          <Box className={boxWrapperClassName}>
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              Expérience d'enseignement et TEFL
            </Typography>
            <div className="h-4" />
            <div className="flex flex-col w-full gap-3">
              <LabelWithText
                className="gap-6"
                display="row"
                label="En quelle année avez-vous commencé à enseigner ?"
                data={<Badge text={tefl.firstYearOfTeaching.toString()} variant="grey" />}
              />
              <LabelWithText
                display="row"
                label="Niveaux les plus enseignés"
                data={t(taughtLevelEnumMapping[tefl.mostLevelTaught])}
              />
              <LabelWithText
                className="gap-10"
                display="row"
                label="Capable d'enseigner dans un autre domaine"
                data={tefl.ableToTeachInAnotherArea ? 'Oui' : 'Non'}
              />
              <LabelWithText
                display="row"
                label="Permis de conduire"
                data={tefl.drivingLicense ? 'Oui' : 'Non'}
              />
              <LabelWithText display="row" label="Voiture" data={tefl.car ? 'Oui' : 'Non'} />
            </div>
          </Box>
          <Box className={boxWrapperClassName}>
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              Adresse
            </Typography>
            <div className="h-4" />
            <div className="flex flex-col w-full gap-3">
              <LabelWithText
                className="gap-6 text-right"
                display="row"
                label="Adresse"
                data={
                  <AddressLink
                    address1={tefl.address1}
                    city={tefl.city}
                    zipCode={tefl.postalCode}
                    country={nativeCountryName(tefl.country)}
                  />
                }
              />
              <LabelWithText
                display="row"
                label="Complément d'adresse"
                data={valueRender(tefl.address2)}
              />
              <LabelWithText display="row" label="Code postal" data={tefl.postalCode} />
              <LabelWithText display="row" label="Ville" data={tefl.city} />
              <LabelWithText display="row" label="Pays" data={nativeCountryName(tefl.country)} />
            </div>
          </Box>
        </div>
      </div>
      <OlTeflSejourTable olId={olId} teflId={tefl.id} />
    </Fragment>
  )
}

export default OlTeflDetails
